import React, {ReactElement} from 'react';
import {graphql, PageProps} from 'gatsby';

import GeneralLayout from '@fo/designSystem/Layout/GeneralLayout/GeneralLayout';
import SEO from '@fo/components/SEO/seo';
import SubscribeToOurNewsletterSection from '@fo/designSystem/SubscribeToOurNewsletterSection/SubscribeToOurNewsletterSection';
import RichTextWithContents from '@fo/designSystem/RichTextWithContents/RichTextWithContents';
import BlogArticleHero from '@fo/components/Blog/BlogArticleHero/BlogArticleHero';
import Avatar from '@fo/designSystem/Avatar/Avatar';
import AuthorBio from '@fo/root/designSystem/molecules/AuthorBio';
import RelatedArticles, {RelatedArticlesBackgroundColor} from '@fo/designSystem/RelatedArticles/RelatedArticles';

import getCanonicalUrl from '@fo/shared-getters/getCanonicalUrl';
import {getPageRoute} from '@fo/utils/routes/getPageRoute';
import LayoutSection from '@fo/designSystem/Layout/LayoutSection/LayoutSection';
import {LayoutBackground} from '@fo/designSystem/Layout/LayoutSection/LayoutSection.types';
import {BlogArticle} from '@fo/shared-types';
import useHandleAffid from '@fo/shared-hooks/useHandleAffid';
import IncludeSignupModal from '@fo/components/SignupModal/IncludeSignupModal';

export interface RelatedBlogArticles {
  nodes: {
    slug: string;
    name: string;
    category: {
      slug: string;
    };
  }[];
}

export const query = graphql`
  query BlogArticle($slug: String!, $categorySlug: String!) {
    contentfulBlogArticle(slug: {eq: $slug}) {
      metaName
      metaDescription
      metaRobots
      date
      createdAt
      name
      slug
      author {
        name
        jobTitle
        slug
        shortBio {
          shortBio
        }
        image {
          title
          gatsbyImageData(
            width: 128
            placeholder: NONE
            formats: [AUTO, WEBP]
            resizingBehavior: NO_CHANGE
            quality: 50
          )
        }
      }
      category {
        name
        slug
      }
      hero {
        image {
          title
          gatsbyImageData(width: 1280, placeholder: BLURRED, formats: [AUTO, WEBP], quality: 60)
        }
      }
      shortDescription {
        internal {
          content
        }
      }
      content {
        raw
        references {
          ... on Node {
            ...Asset
            ...Accordion
            ...EmbeddableAsset
            ...CtaButton
          }
        }
      }
      relatedArticles {
        name
        slug
        category {
          slug
        }
      }
      dialogueWithCta {
        title
        description
        ctaText
        slug
      }
    }

    allContentfulBlogArticle(filter: {category: {slug: {eq: $categorySlug}}, slug: {ne: $slug}}, limit: 12) {
      nodes {
        slug
        name
        category {
          slug
        }
      }
    }
  }
`;

export interface BlogArticlePageProps
  extends PageProps<{contentfulBlogArticle: BlogArticle; allContentfulBlogArticle: RelatedBlogArticles}> {}

function BlogArticlePage({
  data: {contentfulBlogArticle, allContentfulBlogArticle},
}: BlogArticlePageProps): ReactElement {
  useHandleAffid();
  const {
    author,
    category,
    content,
    date,
    hero,
    name,
    shortDescription,
    slug,
    metaName,
    metaDescription,
    metaRobots,
    relatedArticles = [],
    dialogueWithCta,
  } = contentfulBlogArticle;

  return (
    <GeneralLayout>
      <IncludeSignupModal />
      <div className="blogArticle">
        <SEO
          title={metaName || name}
          description={metaDescription || shortDescription.internal.content}
          meta={metaRobots ? [{name: 'robots', content: metaRobots}] : undefined}
          link={[
            {
              rel: 'canonical',
              href: getCanonicalUrl(getPageRoute('blog.article', {categorySlug: category.slug, contentfulSlug: slug})),
            },
          ]}
          slug={getPageRoute('blog.article', {categorySlug: category.slug, contentfulSlug: slug})}
        />

        {hero && (
          <BlogArticleHero
            title={name}
            category={category.name}
            categorySlug={getPageRoute('blog.category.home', {contentfulSlug: category.slug})}
            createdAt={date}
            description={shortDescription.internal.content}
            image={hero.image}
            source={hero?.source}
          />
        )}

        {content && (
          <RichTextWithContents content={content} dialogueWithCta={dialogueWithCta}>
            <AuthorBio
              authorTitle={author?.name}
              authorRole={author?.jobTitle}
              shortBio={author?.shortBio?.shortBio}
              image={<Avatar image={author?.image} />}
              linkToBio={author?.slug ? getPageRoute('author.author', {contentfulSlug: author.slug}) : undefined}
            />
          </RichTextWithContents>
        )}
      </div>

      {(relatedArticles?.length > 0 || allContentfulBlogArticle.nodes.length > 0) && (
        <LayoutSection
          classes={{
            mainInner: 'blogArticle__related',
          }}
          type={LayoutBackground.GRAY}
        >
          <RelatedArticles
            backgroundColor={RelatedArticlesBackgroundColor.WHITE}
            links={[...(relatedArticles || []), ...allContentfulBlogArticle.nodes]
              .slice(0, 12)
              .map((relatedArticle) => ({
                href: getPageRoute('blog.article', {
                  categorySlug: relatedArticle.category.slug,
                  contentfulSlug: relatedArticle.slug,
                }),
                label: relatedArticle.name,
              }))}
            noSorting
          />
        </LayoutSection>
      )}
      <SubscribeToOurNewsletterSection />
    </GeneralLayout>
  );
}

export default BlogArticlePage;
